import type { DeepPartial, Styles } from 'klinecharts'

export const darkTheme: DeepPartial<Styles> = {
  grid: {
    show: true,
    horizontal: { show: true, size: 1, color: '#1e222d', style: 'dashed', dashedValue: [2, 2] },
    vertical: { show: true, size: 1, color: '#1e222d', style: 'dashed', dashedValue: [2, 2] },
  },
  candle: {
    type: 'candle_solid',
    priceMark: {
      show: true,
      high: {
        show: true,
        color: '#d1d4dc',
        textSize: 10,
        textFamily: 'inherit',
        textWeight: 'normal',
        textOffset: 5,
      },
      low: {
        show: true,
        color: '#d1d4dc',
        textSize: 10,
        textFamily: 'inherit',
        textWeight: 'normal',
        textOffset: 5,
      },
      last: {
        show: true,
        upColor: '#26a69a',
        downColor: '#ef5350',
        noChangeColor: '#758696',
        line: { show: true, style: 'dashed', dashedValue: [4, 4], size: 1 },
        text: {
          show: true,
          style: 'fill',
          size: 11,
          paddingLeft: 4,
          paddingTop: 2,
          paddingRight: 4,
          paddingBottom: 2,
          borderStyle: 'solid',
          borderSize: 0,
          borderDashedValue: [2, 2],
          color: '#ffffff',
          // Explicit sans stack (matches the app font). 'inherit' is invalid on
          // a canvas, so klinecharts mis-measures the label box width and leaves
          // slack to the right of the digits — a real family fixes the sizing.
          family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
          weight: 'normal',
          borderRadius: 2,
        },
      },
    },
    bar: {
      upColor: '#26a69a',
      downColor: '#ef5350',
      noChangeColor: '#758696',
      upBorderColor: '#26a69a',
      downBorderColor: '#ef5350',
      noChangeBorderColor: '#758696',
      upWickColor: '#26a69a',
      downWickColor: '#ef5350',
      noChangeWickColor: '#758696',
    },
    tooltip: {
      offsetTop: 4,
      text: { marginLeft: 4 },
      custom: () => [
        { title: 'O: ', value: '{open}' },
        { title: 'H: ', value: '{high}' },
        { title: 'L: ', value: '{low}' },
        { title: 'C: ', value: '{close}' },
      ],
    },
  },
  indicator: {
    lastValueMark: { show: false },
    // Native indicator tooltip is fully suppressed — the React IndicatorLegend
    // renders the data (default) and the hover controls instead.
    tooltip: { showRule: 'none', showName: false, showParams: false },
    bars: [
      {
        upColor: 'rgba(38,166,154,0.7)',
        downColor: 'rgba(239,83,80,0.7)',
        noChangeColor: '#758696',
      },
    ],
    circles: [
      {
        upColor: 'rgba(38,166,154,0.7)',
        downColor: 'rgba(239,83,80,0.7)',
        noChangeColor: '#758696',
      },
    ],
    ohlc: {
      upColor: '#26a69a',
      downColor: '#ef5350',
      noChangeColor: '#758696',
    },
  },
  xAxis: {
    axisLine: { show: true, color: '#2a2e39', size: 1 },
    tickText: { show: true, color: '#787b86', size: 12, family: 'inherit', weight: 'normal' },
    tickLine: { show: true, size: 1, length: 3, color: '#2a2e39' },
  },
  yAxis: {
    axisLine: { show: true, color: '#2a2e39', size: 1 },
    tickText: { show: true, color: '#787b86', size: 12, family: 'inherit', weight: 'normal' },
    tickLine: { show: true, size: 1, length: 3, color: '#2a2e39' },
  },
  crosshair: {
    show: true,
    horizontal: {
      show: true,
      line: { show: true, style: 'dashed', dashedValue: [4, 2], size: 1, color: '#758696' },
      text: {
        show: true,
        style: 'fill',
        color: '#d1d4dc',
        size: 11,
        // 'inherit' is invalid on a canvas — the whole font string is ignored
        // and the label falls back to the default ~10px, rendering smaller than
        // the last-price label. A real family makes the 11px size take effect.
        family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
        weight: 'normal',
        borderStyle: 'solid',
        borderDashedValue: [2, 2],
        borderSize: 0,
        borderColor: 'transparent',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: '#363a45',
      },
    },
    vertical: {
      show: true,
      line: { show: true, style: 'dashed', dashedValue: [4, 2], size: 1, color: '#758696' },
      text: {
        show: true,
        style: 'fill',
        color: '#d1d4dc',
        size: 11,
        // 'inherit' is invalid on a canvas — the whole font string is ignored
        // and the label falls back to the default ~10px, rendering smaller than
        // the last-price label. A real family makes the 11px size take effect.
        family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
        weight: 'normal',
        borderStyle: 'solid',
        borderDashedValue: [2, 2],
        borderSize: 0,
        borderColor: 'transparent',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: '#363a45',
      },
    },
  },
  separator: {
    size: 1,
    color: '#2a2e39',
    fill: true,
    activeBackgroundColor: 'rgba(41,98,255,0.15)',
  },
  // Selected-overlay axis labels (the blue price/time chips). klinecharts'
  // default uses 12px 'Helvetica Neue' — larger than the regular ticks, which
  // render ~10px because their 'inherit' family is invalid on canvas. Pin these
  // to a valid family at the tick size so they match the scale (blue kept).
  overlay: {
    text: {
      size: 11,
      family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
      paddingTop: 2,
      paddingBottom: 2,
    },
    rectText: {
      size: 11,
      family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
} as DeepPartial<Styles>
